import type { Approval, ApprovalVote } from '../types/approval.types';

/** Un paso de la cadena de una regla: una persona o un cargo. */
export type PasoDeRegla = NonNullable<Approval['triggerRule']>['approvers'][number];

/**
 * Cómo se nombra un paso: la persona, o el cargo cuando el paso va por cargo.
 *
 * 🔴 La pantalla leía `ra.user.firstName` a secas. Un paso por cargo no tiene
 * persona (`user: null`) y la pantalla de Aprobaciones quedaba en blanco para
 * todos. Mismo criterio que el aviso tras enviar: el nombre del cargo.
 */
export const etiquetaDelPaso = (ra: PasoDeRegla): string => {
  if (ra.user) return `${ra.user.firstName} ${ra.user.lastName}`.trim();
  return ra.customRole?.name ?? 'Cargo';
};

/**
 * El voto que cumple un paso, si ya lo hay.
 *
 * Por persona: el voto de esa persona. Por cargo: el voto que se firmó CON ese
 * cargo (el servidor lo guarda en `approvedAsRoleId`), de cualquiera que lo tenga.
 */
export const votoDelPaso = (
  ra: PasoDeRegla,
  votes: ApprovalVote[] | undefined
): ApprovalVote | undefined => {
  if (ra.userId) return votes?.find((v) => v.userId === ra.userId);
  if (!ra.customRoleId) return undefined;
  return votes?.find((v) => v.approvedAsRoleId === ra.customRoleId);
};

/** El primer paso de la cadena que todavía no tiene su firma. */
export const siguientePaso = (
  cadena: PasoDeRegla[],
  votes: ApprovalVote[] | undefined
): PasoDeRegla | undefined => cadena.find((ra) => !votoDelPaso(ra, votes));

/** Sólo las personas reales: descarta nulos y lo que no trae `id`. */
export const personasValidas = <T extends { id?: string }>(
  lista: Array<T | null | undefined> | undefined
): T[] => (lista ?? []).filter((u): u is T => !!u?.id);
